import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const formatCurrency = (amount, currency = 'GHS') => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '-';
  return new Intl.NumberFormat('en-GH', { style: 'currency', currency })?.format(num);
};

const formatDate = (date) => {
  try {
    return new Date(date)?.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
};

const METHOD_ICONS = {
  card: 'CreditCard',
  mobile_money: 'Smartphone',
  crypto: 'Bitcoin',
};

const METHOD_LABELS = {
  card: 'Card',
  mobile_money: 'Mobile Money',
  crypto: 'Crypto',
};

// Generate mock transaction history and attempts based on link data
const generateMockData = (link) => {
  const apiStatus = link?.status?.toUpperCase();
  const isPaid = apiStatus === 'PAID';
  const expiryDate = link?.expires_at || link?.expiryDate;
  const createdDate = link?.created_at || link?.createdAt;
  const isExpiredStatus = apiStatus === 'EXPIRED' || (expiryDate && new Date() > new Date(expiryDate));

  const methods = link?.payment_methods?.length
    ? link?.payment_methods
    : link?.allMethods
    ? ['card', 'mobile_money', 'crypto']
    : Object.entries(link?.selectedMethods || {})?.filter(([, v]) => v)?.map(([k]) => k);

  const transactions = isPaid
    ? [
        {
          id: `TXN-${link?.id}-001`,
          date: expiryDate ? new Date(new Date(expiryDate).getTime() - 2 * 60 * 60 * 1000)?.toISOString() : new Date()?.toISOString(),
          method: methods?.[0] || 'card',
          amount: link?.amount,
          currency: link?.currency,
          status: 'success',
          reference: `REF-${Math.random()?.toString(36)?.slice(2, 10)?.toUpperCase()}`,
          customer: link?.billingName || '',
        },
      ]
    : [];

  const attempts = [
    ...(isPaid
      ? [
          {
            id: `ATT-001`,
            date: expiryDate ? new Date(new Date(expiryDate).getTime() - 2.5 * 60 * 60 * 1000)?.toISOString() : new Date()?.toISOString(),
            method: methods?.[0] || 'card',
            status: 'failed',
            reason: 'Insufficient funds',
          },
          {
            id: `ATT-002`,
            date: expiryDate ? new Date(new Date(expiryDate).getTime() - 2 * 60 * 60 * 1000)?.toISOString() : new Date()?.toISOString(),
            method: methods?.[0] || 'card',
            status: 'success',
            reason: null,
          },
        ]
      : isExpiredStatus
      ? [
          {
            id: `ATT-001`,
            date: expiryDate ? new Date(new Date(expiryDate).getTime() - 3 * 60 * 60 * 1000)?.toISOString() : new Date()?.toISOString(),
            method: methods?.[0] || 'card',
            status: 'failed',
            reason: 'Card declined',
          },
          {
            id: `ATT-002`,
            date: expiryDate ? new Date(new Date(expiryDate).getTime() - 1 * 60 * 60 * 1000)?.toISOString() : new Date()?.toISOString(),
            method: methods?.[1] || methods?.[0] || 'card',
            status: 'abandoned',
            reason: 'Session timeout',
          },
        ]
      : []),
  ];

  const views = isPaid ? 8 : isExpiredStatus ? 5 : 3;
  const successCount = isPaid ? 1 : 0;
  const failedCount = attempts?.filter(a => a?.status === 'failed')?.length;
  const conversionRate = views > 0 ? ((successCount / views) * 100)?.toFixed(1) : '0.0';

  const statusTimeline = [
    {
      event: 'Link Created',
      date: createdDate,
      icon: 'Link',
      color: 'primary',
      description: `Payment link generated for ${formatCurrency(link?.amount, link?.currency)}`,
    },
    ...(views > 0
      ? [
          {
            event: 'First View',
            date: createdDate ? new Date(new Date(createdDate).getTime() + 30 * 60 * 1000)?.toISOString() : new Date()?.toISOString(),
            icon: 'Eye',
            color: 'text-blue-500',
            description: `Link opened by a customer`,
          },
        ]
      : []),
    ...attempts?.map((a, i) => ({
      event: a?.status === 'success' ? 'Payment Successful' : a?.status === 'failed' ? 'Payment Failed' : 'Payment Abandoned',
      date: a?.date,
      icon: a?.status === 'success' ? 'CheckCircle' : a?.status === 'failed' ? 'XCircle' : 'Clock',
      color: a?.status === 'success' ? 'text-success' : a?.status === 'failed' ? 'text-error' : 'text-warning',
      description: a?.reason || `Payment attempt via ${METHOD_LABELS?.[a?.method] || a?.method}`,
    })),
    ...(isExpiredStatus
      ? [
          {
            event: 'Link Expired',
            date: expiryDate,
            icon: 'AlertCircle',
            color: 'text-error',
            description: 'Payment link is no longer active',
          },
        ]
      : []),
  ]?.sort((a, b) => new Date(a.date) - new Date(b.date));

  return { transactions, attempts, views, successCount, failedCount, conversionRate, statusTimeline };
};

const AttemptStatusBadge = ({ status }) => {
  const map = {
    success: 'bg-success/10 text-success border-success/20',
    failed: 'bg-error/10 text-error border-error/20',
    abandoned: 'bg-warning/10 text-warning border-warning/20',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${map?.[status] || 'bg-muted text-text-secondary border-border'}`}>
      {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
    </span>
  );
};

const PaymentLinkDetailView = ({ link, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const { transactions, attempts, views, successCount, failedCount, conversionRate, statusTimeline } = generateMockData(link);

  // Resolve KYC fields: support both top-level (new API) and nested address object (legacy)
  const kycFirstName = link?.firstName || link?.address?.first_name || link?.billingFirstName || '';
  const kycLastName = link?.lastName || link?.address?.last_name || link?.billingLastName || '';
  const kycEmail = link?.email || link?.address?.email || link?.billingEmail || '';
  const kycPhone = link?.phone || link?.address?.phone || link?.billingPhone || '';
  const kycAddress = link?.address_line || link?.address?.address || link?.billingAddress || (typeof link?.address === 'string' ? link?.address : '') || '';
  const kycCity = link?.city || link?.address?.city || link?.billingCity || '';
  const kycCountry = link?.country || link?.address?.country || link?.billingCountry || '';
  const kycName = [kycFirstName, kycLastName]?.filter(Boolean)?.join(' ') || link?.billingName || '';

  const apiStatus = link?.status?.toUpperCase();
  const methods = link?.payment_methods || (link?.allMethods
    ? ['card', 'mobile_money', 'crypto']
    : Object.entries(link?.selectedMethods || {})?.filter(([, v]) => v)?.map(([k]) => k));

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'transactions', label: 'Transactions', icon: 'ArrowLeftRight' },
    { id: 'attempts', label: 'Attempts', icon: 'RefreshCw' },
  ];

  const StatusBadgeInline = () => {
    if (apiStatus === 'PAID') return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-500/10 text-blue-600 border-blue-500/20">
        <Icon name="BadgeCheck" size={11} /><span>Paid</span>
      </span>
    );
    if (apiStatus === 'ACTIVE') return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-success/10 text-success border-success/20">
        <Icon name="CheckCircle" size={11} /><span>Active</span>
      </span>
    );
    if (apiStatus === 'EXPIRED') return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-error/10 text-error border-error/20">
        <Icon name="XCircle" size={11} /><span>Expired</span>
      </span>
    );
    if (apiStatus === 'CANCELLED') return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-warning/10 text-warning border-warning/20">
        <Icon name="Ban" size={11} /><span>Cancelled</span>
      </span>
    );
    return (
      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-muted text-text-secondary border-border">
        <Icon name="Circle" size={11} /><span>{link?.status || 'Unknown'}</span>
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-border">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Link" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text-primary">{link?.title}</h2>
              <p className="text-xs text-text-secondary font-mono">{link?.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden sm:flex items-center space-x-1.5">
              <StatusBadgeInline />
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted text-text-secondary transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-1 px-6 pt-4 border-b border-border flex-shrink-0 overflow-x-auto">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-1.5 px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={13} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {/* Conversion Metrics */}
              <div>
                <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Conversion Metrics</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Successful', value: successCount, icon: 'CheckCircle', color: 'bg-success/10 text-success' },
                    { label: 'Failed', value: failedCount, icon: 'XCircle', color: 'bg-error/10 text-error' },
                    { label: 'Conversion Rate', value: `${conversionRate}%`, icon: 'TrendingUp', color: 'bg-primary/10 text-primary' },
                  ]?.map(m => (
                    <div key={m?.label} className="bg-background border border-border rounded-xl p-3 flex flex-col space-y-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${m?.color}`}>
                        <Icon name={m?.icon} size={16} />
                      </div>
                      <p className="text-xl font-bold text-text-primary">{m?.value}</p>
                      <p className="text-xs text-text-secondary">{m?.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Link Details */}
              <div>
                <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Link Details</h3>
                <div className="bg-background border border-border rounded-xl divide-y divide-border">
                  {[
                    { label: 'Amount', value: formatCurrency(link?.amount, link?.currency) },
                    { label: 'Currency', value: link?.currency },
                    { label: 'Description', value: link?.description || '—' },
                    { label: 'Business', value: link?.business_name || '—' },
                    { label: 'Created', value: formatDate(link?.created_at || link?.createdAt) },
                    { label: 'Expires', value: formatDate(link?.expires_at || link?.expiryDate) },
                  ]?.map(row => (
                    <div key={row?.label} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-text-secondary">{row?.label}</span>
                      <span className="text-xs font-medium text-text-primary">{row?.value}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-xs text-text-secondary">Payment Methods</span>
                    <div className="flex items-center space-x-1.5">
                      {methods?.map(m => (
                        <div key={m} className="w-5 h-5 bg-primary/10 rounded flex items-center justify-center" title={METHOD_LABELS?.[m] || m?.replace('_', ' ')}>
                          <Icon name={METHOD_ICONS?.[m] || 'CreditCard'} size={11} className="text-primary" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* KYC / Billing Info */}
              {(kycName || kycEmail || kycPhone || kycAddress || kycCity || kycCountry) && (
                <div>
                  <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">KYC / Customer Info</h3>
                  <div className="bg-background border border-border rounded-xl divide-y divide-border">
                    {[
                      { label: 'Name', value: kycName },
                      { label: 'Email', value: kycEmail },
                      { label: 'Phone', value: kycPhone },
                      { label: 'Address', value: kycAddress },
                      { label: 'City', value: kycCity },
                      { label: 'Country', value: kycCountry },
                    ]?.filter(r => r?.value)?.map(row => (
                      <div key={row?.label} className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-xs text-text-secondary">{row?.label}</span>
                        <span className="text-xs font-medium text-text-primary">{row?.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TRANSACTIONS TAB */}
          {activeTab === 'transactions' && (
            <div>
              <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Transaction History</h3>
              {transactions?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                    <Icon name="ArrowLeftRight" size={20} className="text-text-secondary" />
                  </div>
                  <p className="text-sm font-medium text-text-primary">No transactions yet</p>
                  <p className="text-xs text-text-secondary mt-1">Completed payments will appear here.</p>
                </div>
              ) : (
                <div className="bg-background border border-border rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/30">
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Reference</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Date</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Method</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Amount</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Customer</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {transactions?.map(txn => (
                          <tr key={txn?.id} className="hover:bg-muted/20 transition-colors">
                            <td className="px-4 py-3 font-mono text-xs text-text-secondary">{txn?.reference}</td>
                            <td className="px-4 py-3 text-xs text-text-secondary whitespace-nowrap">{formatDate(txn?.date)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-1.5">
                                <div className="w-5 h-5 bg-primary/10 rounded flex items-center justify-center">
                                  <Icon name={METHOD_ICONS?.[txn?.method] || 'CreditCard'} size={11} className="text-primary" />
                                </div>
                                <span className="text-xs text-text-primary">{METHOD_LABELS?.[txn?.method] || txn?.method}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs font-semibold text-text-primary whitespace-nowrap">
                              {formatCurrency(txn?.amount, txn?.currency)}
                            </td>
                            <td className="px-4 py-3 text-xs text-text-primary">{txn?.customer || '—'}</td>
                            <td className="px-4 py-3">
                              <AttemptStatusBadge status={txn?.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ATTEMPTS TAB */}
          {activeTab === 'attempts' && (
            <div>
              <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Payment Attempts</h3>
              {attempts?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                    <Icon name="RefreshCw" size={20} className="text-text-secondary" />
                  </div>
                  <p className="text-sm font-medium text-text-primary">No payment attempts</p>
                  <p className="text-xs text-text-secondary mt-1">Payment attempts will be recorded here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {attempts?.map((attempt, idx) => (
                    <div key={attempt?.id} className="bg-background border border-border rounded-xl p-4 flex items-start space-x-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        attempt?.status === 'success' ? 'bg-success/10' : attempt?.status === 'failed' ? 'bg-error/10' : 'bg-warning/10'
                      }`}>
                        <Icon
                          name={attempt?.status === 'success' ? 'CheckCircle' : attempt?.status === 'failed' ? 'XCircle' : 'Clock'}
                          size={16}
                          className={attempt?.status === 'success' ? 'text-success' : attempt?.status === 'failed' ? 'text-error' : 'text-warning'}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-semibold text-text-primary">Attempt #{idx + 1}</span>
                            <AttemptStatusBadge status={attempt?.status} />
                          </div>
                          <span className="text-xs text-text-secondary">{formatDate(attempt?.date)}</span>
                        </div>
                        <div className="flex items-center space-x-3 mt-1.5">
                          <div className="flex items-center space-x-1.5">
                            <div className="w-4 h-4 bg-primary/10 rounded flex items-center justify-center">
                              <Icon name={METHOD_ICONS?.[attempt?.method] || 'CreditCard'} size={10} className="text-primary" />
                            </div>
                            <span className="text-xs text-text-secondary">{METHOD_LABELS?.[attempt?.method] || attempt?.method}</span>
                          </div>
                          {attempt?.reason && (
                            <span className="text-xs text-text-secondary">· {attempt?.reason}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0 flex items-center justify-between">
          <p className="text-xs text-text-secondary font-mono truncate max-w-xs hidden sm:block">{link?.link_code || link?.link}</p>
          <button
            onClick={onClose}
            className="ml-auto px-4 py-2 text-sm font-medium text-text-secondary bg-muted hover:bg-muted/80 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentLinkDetailView;
